import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Edit, CheckCircle, X, Camera, Lock } from 'lucide-react';

function ProfileSettings() {
    const { currentUser, logout, updateProfile, updateAvatar } = useAuth();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
        email: currentUser?.email || '',
        username: currentUser?.username || '',
        phone: currentUser?.phone || '',
        city: currentUser?.city || '',
        state: currentUser?.state || '',
        professionalIdentity: currentUser?.professional_identity || currentUser?.professionalIdentity || ''
    });

    // Update form data when currentUser changes
    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: currentUser.firstName || currentUser.first_name || '',
                lastName: currentUser.lastName || currentUser.last_name || '',
                email: currentUser.email || '',
                username: currentUser.username || '',
                phone: currentUser.phone || '',
                city: currentUser.city || '',
                state: currentUser.state || '',
                professionalIdentity: currentUser.professional_identity || currentUser.professionalIdentity || ''
            });
        }
    }, [currentUser]);

    const IDENTITY_PRESETS = [
        { value: 'syndicator',                label: 'Syndicator' },
        { value: 'developer',                 label: 'Developer' },
        { value: 'general_contractor',        label: 'General Contractor / Builder' },
        { value: 'architect',                 label: 'Architect / Designer' },
        { value: 'asset_manager',             label: 'Asset Manager' },
        { value: 'property_manager',          label: 'Property Manager' },
        { value: 'compliance_professional',   label: 'Compliance Professional' },
        { value: 'hfa_staff',                 label: 'State HFA Staff' },
        { value: 'pha_staff',                 label: 'Public Housing Authority Staff' },
        { value: 'hud_contractor',            label: 'HUD Contractor / Monitor' },
        { value: 'tax_credit_accountant',     label: 'Tax Credit Accountant / CPA' },
        { value: 'housing_attorney',          label: 'Housing Attorney' },
        { value: 'investor',                  label: 'Investor' },
        { value: 'lender',                    label: 'Lender' },
        { value: 'loan_officer',              label: 'Loan Officer / Underwriter' },
        { value: 'appraiser',                 label: 'Appraiser' },
        { value: 'real_estate_broker',        label: 'Real Estate Broker' },
        { value: 'consultant',                label: 'LIHTC / Housing Consultant' },
        { value: 'nonprofit_executive',       label: 'Non-profit Executive' },
        { value: 'policy_researcher',         label: 'Housing Policy Researcher / Academic' },
        { value: 'resident_services',         label: 'Resident Services Coordinator' },
        { value: 'resident',                  label: 'Resident / Tenant' },
        { value: 'homeowner_hoa',             label: 'Homeowner / HOA' }
    ];
    const isCustomIdentity = !!formData.professionalIdentity
        && !IDENTITY_PRESETS.some(o => o.value === formData.professionalIdentity);
    const [useCustomIdentity, setUseCustomIdentity] = useState(isCustomIdentity);
    useEffect(() => { setUseCustomIdentity(isCustomIdentity); }, [isCustomIdentity]);

    const identityDisplay = (() => {
        if (!formData.professionalIdentity) return null;
        const preset = IDENTITY_PRESETS.find(o => o.value === formData.professionalIdentity);
        return preset ? preset.label : formData.professionalIdentity;
    })();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        if (!file.type.match('image.*')) {
            setError('Please select an image file');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const result = await updateAvatar(formData);

            if (result.success) {
                setSuccess('Profile photo updated successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Failed to update profile photo');
            }
        } catch (err) {
            setError('An error occurred while updating profile photo');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const result = await updateProfile(formData);

            if (result.success) {
                setIsEditing(false);
                setSuccess('Profile updated successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Failed to update profile');
            }
        } catch (err) {
            setError('An error occurred while updating profile');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Success/Error message */}
            {success && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 flex items-center">
                    <CheckCircle size={18} className="mr-2" />
                    {success}
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 flex items-center">
                    <X size={18} className="mr-2" />
                    {error}
                </div>
            )}

            {/* Profile card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                                {currentUser?.avatarUrl ? (
                                    <img
                                        src={currentUser.avatarUrl}
                                        alt={`${currentUser.first_name} ${currentUser.last_name}`}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    (currentUser?.firstName || currentUser?.first_name)?.charAt(0) || 'U'
                                )}
                            </div>

                            {isEditing && (
                                <>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="absolute bottom-0 right-0 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors"
                                        disabled={loading}
                                    >
                                        <Camera size={14} />
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileChange}
                                        disabled={loading}
                                    />
                                </>
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-unna font-semibold">{currentUser?.firstName || currentUser?.first_name} {currentUser?.lastName || currentUser?.last_name}</h2>
                            <p className="text-gray-500 dark:text-gray-400 font-rasa">{currentUser?.email}</p>
                        </div>
                    </div>

                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 font-rasa"
                        >
                            <Edit size={16} />
                            <span>Edit Profile</span>
                        </button>
                    )}
                </div>

                {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-rasa font-medium mb-1">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-rasa font-medium mb-1">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-rasa font-medium mb-1">Username</label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-rasa font-medium mb-1">Phone</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-rasa font-medium mb-1">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-rasa font-medium mb-1">State</label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-rasa font-medium mb-1">Professional identity</label>
                            {!useCustomIdentity ? (
                                <select
                                    value={formData.professionalIdentity}
                                    onChange={(e) => {
                                        if (e.target.value === '__custom__') {
                                            setUseCustomIdentity(true);
                                            setFormData(prev => ({ ...prev, professionalIdentity: '' }));
                                        } else {
                                            setFormData(prev => ({ ...prev, professionalIdentity: e.target.value }));
                                        }
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">— Select —</option>
                                    {IDENTITY_PRESETS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                    <option value="__custom__">Other / custom…</option>
                                </select>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        name="professionalIdentity"
                                        value={formData.professionalIdentity}
                                        onChange={handleChange}
                                        maxLength={80}
                                        placeholder="e.g. Housing policy researcher"
                                        className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setUseCustomIdentity(false);
                                            setFormData(prev => ({ ...prev, professionalIdentity: '' }));
                                        }}
                                        className="px-3 py-2 text-sm border rounded-lg dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        Back to list
                                    </button>
                                </div>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-rasa">
                                Helps JPRO tailor news and recommendations to your role. Pick a preset or type your own.
                            </p>
                        </div>

                        <div className="border-t dark:border-gray-700 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard/password-change')}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-rasa"
                            >
                                <Lock size={16} />
                                <span>{currentUser?.has_password === false ? 'Set Password' : 'Change Password'}</span>
                            </button>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-rasa"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-rasa"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="space-y-4">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <h3 className="font-unna font-medium mb-3">Profile Information</h3>

                            <div className="grid grid-cols-1 gap-4">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400 font-rasa">Username:</span>
                                    <span className="font-rasa font-medium">{currentUser?.username}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400 font-rasa">Email:</span>
                                    <span className="font-rasa font-medium">{currentUser?.email}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400 font-rasa">Name:</span>
                                    <span className="font-rasa font-medium">{currentUser?.firstName || currentUser?.first_name} {currentUser?.lastName || currentUser?.last_name}</span>
                                </div>

                                {currentUser?.phone && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400 font-rasa">Phone:</span>
                                        <span className="font-rasa font-medium">{currentUser?.phone}</span>
                                    </div>
                                )}

                                {currentUser?.city && currentUser?.state && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400 font-rasa">Location:</span>
                                        <span className="font-rasa font-medium">{currentUser?.city}, {currentUser?.state}</span>
                                    </div>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400 font-rasa">Professional identity:</span>
                                    <span className="font-rasa font-medium">
                                        {identityDisplay || <em className="text-gray-400">Not set</em>}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center mt-6">
                            <button
                                onClick={logout}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-rasa"
                            >
                                <LogOut size={18} />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProfileSettings;
